import { useEffect, useState } from 'react';
import type { ScriptVersion, ScriptVersionFull, VersionReason } from '../../shared/types';
import { VERSION_REASON_LABELS } from '../../shared/types';
import { adminApi } from '../lib/api';
import Eyebrow from './Eyebrow';
import Button from './Button';
import { Editor } from './Editor/Editor';
import { parseDocJson } from '../lib/parseDoc';

interface Props {
  scriptId: string;
  open: boolean;
  onClose: () => void;
  onRestored: () => void;
}

export default function VersionHistoryPanel({ scriptId, open, onClose, onRestored }: Props) {
  const [versions, setVersions] = useState<ScriptVersion[] | null>(null);
  const [selected, setSelected] = useState<ScriptVersionFull | null>(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingNew, setSavingNew] = useState(false);

  useEffect(() => {
    if (!open) return;
    void refresh();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function refresh() {
    setVersions(null);
    setSelected(null);
    try {
      const res = await adminApi.listVersions(scriptId);
      setVersions(res.versions);
    } catch {
      setError('Could not load version history.');
    }
  }

  async function selectVersion(v: ScriptVersion) {
    setLoadingSelected(true);
    setError(null);
    try {
      const res = await adminApi.getVersion(scriptId, v.id);
      setSelected(res.version);
    } catch {
      setError('Could not load this version.');
    } finally {
      setLoadingSelected(false);
    }
  }

  async function saveCurrent() {
    setSavingNew(true);
    setError(null);
    try {
      await adminApi.saveVersion(scriptId);
      await refresh();
    } catch {
      setError('Could not save snapshot.');
    } finally {
      setSavingNew(false);
    }
  }

  async function restore() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.restoreVersion(scriptId, selected.id);
      onRestored();
      onClose();
    } catch {
      setError('Could not restore this version.');
      setBusy(false);
    }
  }

  if (!open) return null;

  // A snapshot that can't be parsed must neither render (it would throw inside
  // the editor) nor be restorable — restoring would copy the corruption over
  // the live document.
  const selectedDoc = parseDocJson(selected?.doc_json);

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md z-50 animate-fade-in flex items-stretch justify-end"
      onClick={busy ? undefined : onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="
          w-full max-w-4xl bg-page border-l border-line
          flex flex-col animate-rise-subtle
        "
        style={{ animationDuration: '300ms' }}
      >
        {/* Header */}
        <header className="px-6 py-4 border-b border-line flex items-center justify-between gap-4">
          <div>
            <Eyebrow accent>// Version history</Eyebrow>
            <h2 className="font-display font-bold text-[22px] tracking-[-0.01em] mt-1.5">
              Restore a previous draft
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="whitespace-nowrap md:whitespace-normal"
              onClick={saveCurrent}
              disabled={savingNew || busy}
            >
              {savingNew ? 'Saving…' : 'Save snapshot'}
            </Button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label="Close"
              className="text-muted hover:text-fg transition-colors text-xl leading-none px-2"
            >
              ×
            </button>
          </div>
        </header>

        {/* Two-pane: list + preview */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-0">
          {/* List */}
          <nav className="border-r border-line overflow-y-auto bg-card">
            {versions === null ? (
              <ul className="p-3 space-y-2">
                {[0, 1, 2, 3].map((i) => (
                  <li key={i} className="h-14 rounded-sm skeleton" />
                ))}
              </ul>
            ) : versions.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-faint text-[13px] leading-[1.55]">
                  No snapshots yet. We auto-snapshot on status changes and accepted suggestions, or
                  you can save one manually with the button above.
                </p>
              </div>
            ) : (
              <ul>
                {versions.map((v) => (
                  <li key={v.id}>
                    <button
                      type="button"
                      onClick={() => selectVersion(v)}
                      className={`
                        w-full text-left px-4 py-3 border-b border-line/60
                        transition-colors duration-150
                        ${selected?.id === v.id ? 'bg-accent/10' : 'hover:bg-elevated'}
                      `}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.12em] ${v.reason === 'publish_quiet' ? 'text-muted' : 'text-accent'}`}>
                          {versionLabel(v)}
                        </span>
                        {/* 2A: the save counter lives here as #41, so it never
                            reads as a client version next to "Published v3". */}
                        <span className="font-mono text-[10px] text-faint">
                          #{v.doc_version}
                        </span>
                      </div>
                      <div className="text-[13px] text-fg font-medium truncate">
                        {v.title || 'Untitled'}
                      </div>
                      <div className="text-[11px] text-faint mt-0.5 flex items-center gap-2">
                        <span>{absoluteDate(v.created_at)}</span>
                        {v.reason_note && (
                          <>
                            <span className="text-faint/50">·</span>
                            <span className="italic truncate">{v.reason_note}</span>
                          </>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          {/* Preview */}
          <div className="overflow-y-auto">
            {!selected && !loadingSelected && (
              <div className="h-full flex items-center justify-center p-12">
                <p className="text-faint text-[13px] text-center max-w-xs leading-[1.55]">
                  {versions && versions.length === 0
                    ? 'Snapshots will appear here as you advance status or accept suggestions.'
                    : 'Pick a version on the left to preview it.'}
                </p>
              </div>
            )}
            {loadingSelected && (
              <div className="p-6 space-y-3">
                <div className="h-4 w-1/3 bg-card rounded skeleton" />
                <div className="h-3 w-2/3 bg-card rounded skeleton" />
                <div className="h-3 w-1/2 bg-card rounded skeleton" />
              </div>
            )}
            {selected && !loadingSelected && (
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <Eyebrow accent>
                      // {versionLabel(selected)} · #{selected.doc_version}
                    </Eyebrow>
                    <p className="text-fg text-[14px] mt-1.5">
                      <span className="font-medium">{selected.title || 'Untitled'}</span>
                      <span className="text-faint"> · {absoluteDate(selected.created_at)}</span>
                    </p>
                    {selected.reason_note && (
                      <p className="text-muted text-[12px] mt-1 italic">{selected.reason_note}</p>
                    )}
                    {sentBullets(selected.summary_json).length > 0 && (
                      <div className="mt-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">Sent with this version</p>
                        <ul className="mt-1.5 space-y-1 text-[13px] text-fg list-disc pl-4">
                          {sentBullets(selected.summary_json).map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                  {confirming ? (
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <p className="text-[12px] text-muted text-right max-w-xs leading-snug">
                        Restoring replaces your working copy only. The client keeps reading the
                        published version until you publish. We'll snapshot your working copy first.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirming(false)}
                          disabled={busy}
                        >
                          Cancel
                        </Button>
                        <Button variant="primary" size="sm" onClick={restore} disabled={busy}>
                          {busy ? 'Restoring…' : 'Yes, restore'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      glow
                      onClick={() => setConfirming(true)}
                      disabled={busy || !selectedDoc}
                    >
                      Restore to working copy
                    </Button>
                  )}
                </div>

                <div className="bg-card rounded-md border border-line px-6 py-8">
                  {selectedDoc ? (
                    <div data-readonly="true">
                      <Editor
                        key={selected.id}
                        initialDoc={selectedDoc}
                        onUpdate={() => {}}
                        editable={false}
                      />
                    </div>
                  ) : (
                    <p className="text-accent text-[13px] leading-[1.55]">
                      This snapshot's document couldn't be read, so it can't be previewed or
                      restored. Try an adjacent snapshot.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <footer className="px-6 py-3 border-t border-accent/40 bg-accent/8">
            <p className="text-accent text-[13px]">{error}</p>
          </footer>
        )}
      </aside>
    </div>
  );
}

function absoluteDate(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Published v3" / "Quiet update, v2" from the stored round (2A), never
 *  parsed from the note; other reasons keep their shared label. */
function versionLabel(v: { reason: string; published_round?: number | null }): string {
  if (v.reason === 'publish_round') return v.published_round ? `Published v${v.published_round}` : 'Published';
  if (v.reason === 'publish_quiet') return v.published_round ? `Quiet update, v${v.published_round}` : 'Quiet update';
  return VERSION_REASON_LABELS[v.reason as VersionReason] ?? v.reason;
}

function sentBullets(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? parsed.filter((b): b is string => typeof b === 'string') : [];
  } catch {
    return [];
  }
}
